import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import {
	BasicFilterExpression,
	DataOptions,
	DataResponse,
	DataService,
	DoorstroomMeasure,
	ExportDataOptions,
	FilterExpression,
} from '../../services/data.service';
import { Observable } from 'rxjs';
import { ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import { att, attParent, percOfParent, percOfRow } from '../../services/measures';
import { AttrPath, ExamenstatusMetPrognose, Table } from '@cumlaude/metadata';
import { maxOverMapped, noAgg0, sumOver0 } from '../../services/aggregation';
import { Path } from '../../services/data-tree';
import { formatPercent } from '@angular/common';
import { generateCssClassForString } from '@cumlaude/shared-utils';
import { formatDecimal } from '@cumlaude/shared-pipes';
import { OPTIONAL_2_DECIMALS } from '../doorstroom/doorstroom.component';
import { BarInfo } from '../../services/stacked-bars';
import { range } from 'lodash-es';
import { DataRow } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { Attributes, SelectionConfig } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext, DataRequestContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { PartitionMeasure, VbarchartTableComponent } from '../../shared/dashboard/vbarchart-table/vbarchart-table.component';
import { DashboardVariant } from '../../services/weergave-opties';
import { TooltipElement } from '@cumlaude/shared-components-overlays';
import { PsName } from '../../services/page-state.service';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

interface GeslaagdI extends Attributes {
	ds_fun_nm_examenstatus_met_prognose: ExamenstatusMetPrognose;
	ds_m_is_examenprognose: string;
	ds_m_nr_leerlingen: number;
	ds_m_nr_weging: number;
	ds_m_nr_geslaagd_weging: number;
	ds_m_nr_geslaagd_leerlingen: number;
}

interface GeslaagdA extends Attributes {
	geslaagd_weging: number;
	geslaagd_lln: number;
	ds_m_is_examenprognose: number;
	ds_m_nr_leerlingen: number;
	ds_m_nr_weging: number;
}

@Component({
	selector: 'app-geslaagd',
	templateUrl: './geslaagd.component.html',
	styleUrls: ['./geslaagd.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		BarchartTableComponent,
		VbarchartTableComponent,
		FilterBarComponent,
		LegendaComponent,
		ToggleButtonsComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeslaagdComponent extends BarchartTableConfig<GeslaagdI, GeslaagdA> {
	protected readonly dataService = inject(DataService);

	subgroups: AttrPath[] = [['ds_fun_nm_examenstatus_met_prognose']];

	historieGroups = computed(() => this.groeperingService.huidigeGroeperingen().slice(0, -1));

	historieSubgroups = computed(() => {
		return [...this.groeperingService.huidigeGroeperingen().slice(-1), ['ds_nm_schooljaar_van'] as AttrPath, ...this.subgroups];
	});

	override availableGroups: AttrPath[] = [
		['ds_fk_ll', 'll_nm_basisschooladvies'],
		['ds_nm_klas_van'],
		['ds_nr_leerjaar_van'],
		['ds_fk_ilt_van', 'ilt_nm_niveau'],
		['ds_nm_opleiding_van'],
		['ds_fk_ilt_van', 'ilt_nm_opleiding'],
		['ds_fk_ilt_van', 'ilt_abb_profiel'],
		['ds_fk_vs_van', 'vs_nm_vestiging'],
		['ds_fk_ll', 'll_nm_geslacht'],
	];

	actueelFilters: FilterName[] = [
		'ds_nm_schooljaar_van',
		'ds_fk_lb_van.lb_co_brin',
		'ds_fk_vs_van.vs_nm_vestiging',
		'ds_fun_nm_examenstatus_met_prognose',
	];

	historieFilters: FilterName[] = [
		'x_doorstroom_schooljaar_historie', //
		'x_doorstroom_multiselect_schooljaar',
		...this.actueelFilters,
	];

	permanentFilterExpressions: FilterExpression[] = [
		new BasicFilterExpression<number>(['ds_fk_lb_van', 'lb_is_examenopleiding'], 1),
		new BasicFilterExpression<number>(['ds_is_relevante_doorstroom'], 1),
		new BasicFilterExpression(['ds_nr_weging'], 0, '>'),
		new BasicFilterExpression<number>(['ds_fk_ilt_van', 'ilt_is_internationaal'], 0),
		new BasicFilterExpression<number>(['ds_fk_ilt_van', 'ilt_is_vakmanschap'], 0),
		new BasicFilterExpression<number>(['ds_fk_ilt_van', 'ilt_is_vavo'], 0),
		new BasicFilterExpression<number>(['ds_fun_is_pro_volgend_sj'], 0),
		new BasicFilterExpression<number>(['ds_fun_is_uitstroom_hoger_onderwijs_geen_uitslag'], 0),
		new BasicFilterExpression<number>(['ds_fun_is_uitstroom_speciaal_onderwijs'], 0),
	];

	variant = this.qp.signal('variant');

	factTable = Table.fac_ds_doorstroom;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getDoorstroomData({
			...options,
			m: [DoorstroomMeasure.LEERLINGEN, DoorstroomMeasure.GESLAAGD_AANTALLEN],
			r: range(0, this.getRowLevel() + 1),
		});
	}

	override getExportData(options: ExportDataOptions) {
		return this.dataService.getDoorstroomExportData(options);
	}

	override makeBar(attrs: GeslaagdI, path: Path<GeslaagdA, number[]>, context: DashboardContext<GeslaagdI, GeslaagdA, GeslaagdComponent>): BarInfo {
		const { ds_m_nr_weging, ds_fun_nm_examenstatus_met_prognose } = attrs;
		const percentage = formatPercent(percOfParent('ds_m_nr_weging', 'ds_m_nr_weging')(path), 'nl-NL');
		const total = attParent<'ds_m_nr_weging', GeslaagdA>('ds_m_nr_weging')(path);
		const examenstatus = this.displayService.display(ds_fun_nm_examenstatus_met_prognose);

		const tooltipElements: TooltipElement[] = [
			{ label: `Examenstatus:`, value: `${examenstatus}` },
			{ label: `Leerlingen:`, value: `${formatDecimal(ds_m_nr_weging, OPTIONAL_2_DECIMALS)} van ${formatDecimal(total, OPTIONAL_2_DECIMALS)}` },
			{ label: `Leerlingen (%):`, value: `${percentage}` },
		];

		return {
			...super.makeBar(attrs, path, context),
			size: ds_m_nr_weging,
			description: examenstatus,
			className: generateCssClassForString(ds_fun_nm_examenstatus_met_prognose),
			tooltip: tooltipElements,
		};
	}

	protected override singleAggregators = {
		geslaagd_weging: sumOver0<'ds_m_nr_geslaagd_weging', GeslaagdI, number>('ds_m_nr_geslaagd_weging'),
		geslaagd_lln: noAgg0<'ds_m_nr_geslaagd_leerlingen', GeslaagdI>('ds_m_nr_geslaagd_leerlingen'),
		ds_m_is_examenprognose: maxOverMapped<GeslaagdI>((v) => Number(v.ds_m_is_examenprognose)),
		ds_m_nr_leerlingen: noAgg0<'ds_m_nr_leerlingen', GeslaagdI>('ds_m_nr_leerlingen'),
		ds_m_nr_weging: sumOver0<'ds_m_nr_weging', GeslaagdI, number>('ds_m_nr_weging'),
	};

	override createMeasureColumns(context: DashboardContext<GeslaagdI, GeslaagdA, GeslaagdComponent>): ColumnDef<DataRow<GeslaagdA>>[] {
		if (this.variant() === DashboardVariant.HISTORIE) return [];

		return [
			this.createLeerlingColumn(
				'Percentage',
				percOfRow('geslaagd_weging', 'ds_m_nr_weging'),
				{ context, dataType: 'percentage' },
				att('geslaagd_lln'),
				this.getGeslaagdFilters()
			),
			this.createLeerlingColumn(
				'Geslaagd',
				att('geslaagd_weging'),
				{ context, format: OPTIONAL_2_DECIMALS },
				att('geslaagd_lln'),
				this.getGeslaagdFilters()
			),
			this.createLeerlingColumn('Leerlingen', att('ds_m_nr_weging'), { context, format: OPTIONAL_2_DECIMALS }, att('ds_m_nr_leerlingen')),
		];
	}

	override isHistorieBatchVariant(): boolean {
		return this.variant() === DashboardVariant.HISTORIE && this.groeperingService.huidigeAantalGroeperingen() > 0;
	}

	private getGeslaagdFilters() {
		return [
			new BasicFilterExpression(
				['ds_fun_nm_examenstatus_met_prognose'],
				[ExamenstatusMetPrognose.GESLAAGD, ExamenstatusMetPrognose.GESLAAGD_PROGNOSE],
				'in'
			),
		];
	}

	partitionMeasure: PartitionMeasure<GeslaagdA> = { type: 'percentage', getValue: percOfRow('geslaagd_weging', 'ds_m_nr_weging') };

	override enrichTableModel(_context: DashboardContext<GeslaagdI, GeslaagdA, GeslaagdComponent>, tableModel: TableModel<DataRow<GeslaagdA>>) {
		tableModel.showFooters = this.variant() === DashboardVariant.ACTUEEL;
	}

	getRowLevel() {
		return this.variant() === DashboardVariant.ACTUEEL ? this.groeperingService.huidigeAantalGroeperingen() : this.historieGroups().length;
	}

	override onContextCreated(context: DashboardContext<GeslaagdI, GeslaagdA, GeslaagdComponent>) {
		this.pageStateService.dispatch(PsName.prognose, String(Boolean(context.dataRoot?.a.ds_m_is_examenprognose)));
	}

	override getSelectionConfig(context: DataRequestContext<GeslaagdA>): SelectionConfig<GeslaagdA> | undefined {
		return { ...super.getSelectionConfig(context)!, getSize: att('ds_m_nr_leerlingen') };
	}

	protected readonly DashboardVariant = DashboardVariant;
}
